import { useNavigate } from '@tanstack/react-router';
import { RowClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from 'primereact/button';
import { useCallback, useRef, useState } from 'react';
import { FundGetData } from '~/api/def/endpoints/fundEndpoint';
import {
  fundAnalyticsListAtom,
  selectedFundAtom,
} from '~/atoms/fundAnalyticsAtom';
import { FundSettingsDialog } from '~/components/forms/FundSettingsDialog';
import { WidgetActions } from '~/components/ui/common/WidgetActions';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { colTypesWidths } from '~/components/ui/tables/ag/columnTypes';
import { colDefsLinkedDocumentsGet } from '~/components/ui/tables/ag/fundListDashboard/colDefsLinkedDocuments';

export type FundListDashboardProps = {
  refetchFundListAnalytics: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId?: string;
};
export const FundListDashboard = ({
  refetchFundListAnalytics,
  handleRemoveWidget,
  widgetId,
}: FundListDashboardProps) => {
  const navigate = useNavigate();
  const setSelectedFund = useSetAtom(selectedFundAtom);
  const selectedFund = useAtomValue(selectedFundAtom);
  const { loading, fundList } = useAtomValue(fundAnalyticsListAtom);

  const handleSelectFund = (selectedFund: FundGetData) => {
    setSelectedFund(selectedFund);
  };

  const gridRef = useRef<AgGridReact<FundGetData>>(null);
  const colDefsLinkedDocuments = colDefsLinkedDocumentsGet({
    onFund: (selectedFund) => handleSelectFund(selectedFund),
  });
  const [fundSettingsDialogVisible, setFundSettingsDialogVisible] =
    useState(false);
  const getRowStyle = useCallback(
    (params: RowClassParams<FundGetData, unknown>) => {
      if (selectedFund?.id === params.data?.id) {
        return { backgroundColor: 'rgba(0, 0, 0, 0.4)' };
      }
      return undefined;
    },
    [selectedFund?.id],
  );

  const removeWidget = () => {
    widgetId && handleRemoveWidget && handleRemoveWidget(widgetId);
  };

  return (
    <div className="hover-container h-full">
      <div className="flex justify-content-between align-items-center">
        <div className="w-full flex justify-content-between align-items-center">
          <div className="flex align-items-center gap-3">
            <h3 className="mb-0"> Fund List</h3>
            {selectedFund && (
              <>
                <Button
                  label="Fund Settings"
                  icon="pi pi-sliders-h"
                  className="w-10rem h-2rem"
                  onClick={() => {
                    setFundSettingsDialogVisible(true);
                  }}
                />
                <Button
                  label="Go to List of Portfolio Companies"
                  icon="pi pi-angle-double-right"
                  className="h-2rem"
                  onClick={() =>
                    navigate({
                      to: `/fund/portfolioCompanies/${selectedFund.id}?fundName=${selectedFund.name}`,
                    })
                  }
                />
              </>
            )}
          </div>
          <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
        </div>
      </div>
      <div
        className="mt-3"
        style={{ height: 'calc(100% - 3.4rem)', marginLeft: '0' }}
      >
        <AgGridReact
          rowData={fundList}
          ref={gridRef}
          loading={loading}
          columnDefs={colDefsLinkedDocuments}
          columnTypes={colTypesWidths}
          tooltipShowDelay={0}
          theme={agTheme}
          suppressCellFocus
          onGridReady={() => gridRef.current?.api.sizeColumnsToFit()}
          autoSizeStrategy={{
            type: 'fitGridWidth',
            defaultMinWidth: 100,
          }}
          getRowStyle={getRowStyle}
        />
      </div>
      {selectedFund && (
        <FundSettingsDialog
          isVisible={fundSettingsDialogVisible}
          onHide={() => setFundSettingsDialogVisible(false)}
          selectedFund={selectedFund}
          refetchFundListAnalytics={refetchFundListAnalytics}
        />
      )}
    </div>
  );
};
